import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {memoize} from 'decko';
import {saveAs} from 'file-saver/FileSaver';
import {ConfigurationService, VariantsService, TranslationsService} from '../../pages-services';
import {ChangesSubject, DataChangesObserver, LOCALE_CONFIG, TitleService} from '@common-lib';
import {ActivatedRoute} from '@angular/router';
import {map} from 'rxjs/operators/map';
import {mergeMap, toArray} from 'rxjs/operators';
import {from} from 'rxjs/observable/from';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';

/**
 * @deprecated
 */
@Component({
    selector: 'app-configuration',
    templateUrl: './configuration.component.html',
    styleUrls: ['./configuration.component.scss']
})
export class ConfigurationComponent implements OnInit {
    modalRef: BsModalRef;
    exportSettingsModalRef: BsModalRef;

    changesSubject: ChangesSubject;
    countries = [];
    locales = [];
    selectedLanguage = LOCALE_CONFIG.DEFAULT_LANGUAGE;
    selectedLocale = LOCALE_CONFIG.DEFAULT_LOCALE;
    changesCount = 0;
    dataSaving = false;
    haveChanges = false;
    checkedItems: any = {};
    editItems: any = {};
    navigation = {};
    isExporting;
    isUploading;
    productsToExport: any[] = [];
    showPrice = true;
    state: any = {};
    variants = [];
    selectedVariantsByVariantGroup = [];
    variantsById = [];
    choices = [
        {name: 'Muiltichoice', id: true},
        {name: 'Singlechoice', id: false},
    ];

    deletedItems: any = {
        products: [],
        categories: [],
        navigations: [],
        subcategories: [],
        views: [],
    };

    complianceList = {
        products: 'product',
        categories: 'category',
        navigations: 'navigation',
        subcategories: 'subcategory',
        views: 'view',
        variantGroups: 'variant-groups',
    };

    @ViewChild('confirmationTemplate')
    confirmationTemplateRef: TemplateRef<any>;

    @ViewChild('exportSettingsFormTemplate')
    exportSettingsFormTemplate: TemplateRef<any>;

    constructor(private title: TitleService,
                private route: ActivatedRoute,
                private configurationService: ConfigurationService,
                private variantsService: VariantsService,
                private translateService: TranslateService,
                private datachanges: DataChangesObserver,
                private toasterService: ToasterService,
                private translationsService: TranslationsService,
                private modalService: BsModalService) {
    }

    ngOnInit() {
        this.title.setTitle(this.route.snapshot.data.title ? this.route.snapshot.data.title : '');
        this.translationsService.getLocalizations().subscribe((data) => {
            this.countries = data;

            this.locales = data.reduce((acc, cur, i) => {
                cur.locales.forEach((locale) => {
                    acc = [...acc, {
                        localeName: locale,
                        languageCode: cur.language,
                        languageTitle: cur.title,
                    }];
                });
                return acc;
            }, []);

            this.getConfigData();
        });
    }

    getConfigData() {
        const locale = this.getCurrentLocale();

        this.variantsService.getList(locale)
            .pipe(
                mergeMap(({variants}) => from(variants)),
                mergeMap((variant: any) =>
                    this.translateService.get(variant.title).pipe(map(title => {
                            variant.title = title;
                            return variant;
                        }),
                    )
                ),
                toArray(),
            ).subscribe(variants => {
            this.variants = variants;
            this.variants.forEach(variant => {
                const {id, name, title, products} = variant;
                this.variantsById[id] = {id, name, title, products};
            });
        });

        this.configurationService.getList('products', locale).subscribe(({result: {products}}) => {
            this.state.productsById = {};
            products.forEach(product => {
                this.state.productsById[product.id] = product.title;
            });
            this.state.products = this.createChangesObservation(products);
        });

        this.configurationService.getList('navigations', locale).subscribe(({result}) => {
            this.state.navigations = this.createChangesObservation(result);
        });

        this.configurationService.getList('categories', locale).subscribe(({result: {categories}}) => {
            this.state.categoryById = {};
            categories.forEach(category => {
                const {title, productId, id} = category;

                this.state.categoryById[id] = {title, productId};

            });
            this.state.categories = this.createChangesObservation(categories);
        });

        this.configurationService.getList('subcategories', locale).subscribe(({result}) => {
            this.state.subcategoryById = {};
            result.subcategories.forEach(subcategory => {
                const {name, productId, id} = subcategory;
                this.state.subcategoryById[id] = {name, productId};
            });
            this.state.subcategories = this.createChangesObservation(result.subcategories);
        });

        this.configurationService.getList('views', locale).subscribe(({result}) => {
            this.state.viewById = {};
            result.views.forEach(view => {
                const {name, id} = view;
                this.state.viewById[id] = {name};
            });
            this.state.views = this.createChangesObservation(result.views);
        });

        this.configurationService.getList('variant-groups', locale).subscribe(({result}) => {
            this.state.variantGroupById = {};
            result.variantGroups = result.variantGroups.map(variantGroup => {
                const {id, name} = variantGroup;
                this.state.variantGroupById[id] = {name};
                variantGroup.variantIds = variantGroup.variants.map(v => v.id);
                this.selectedVariantsByVariantGroup[id] = variantGroup.variantIds;
                return variantGroup;
            });
            this.state.variantGroups = this.createChangesObservation(result.variantGroups);
        });

        this.dataSaving = false;
    }

    localeOpen(event) {
        if (this.changesCount > 0) {
            this.modalRef = this.modalService.show(this.confirmationTemplateRef, {class: 'modal-sm'});
        }
    }

    localeChanged(event) {
        this.getConfigData();
    }

    confirm() {
        if (this.modalRef) {
            this.modalRef.hide();
        }

        this.getConfigData();
    }

    decline() {
        this.modalRef.hide();
    }

    @memoize()
    getDropdownList(groupName, keyName, productId) {
        return this.state[groupName].filter(item => item[keyName] === productId || !item[keyName]);
    }

    @memoize()
    getVariantsDropdownList(variantGroupItem) {
        variantGroupItem = JSON.parse(variantGroupItem);
        let variants = this.variants
            .filter(item => item.subcategoryId === variantGroupItem.subcategoryId || !item.subcategoryId)
            .map(item => {
                item.disabled = false;
                return item;
            });
        console.log('variants: ', variants);
        console.log('variantGroupId, subcategoryId: ', variantGroupItem.id, variantGroupItem.subcategoryId);

        this.selectedVariantsByVariantGroup
            .filter((selectedVariantIds, index) => variantGroupItem.id !== index)
            .forEach((selectedVariantIds) => {
                variants = variants.map(item => {
                    if (selectedVariantIds.includes(item.id)) {
                        item.disabled = true;
                    }
                    return item;
                });
        });
        console.log('variants: ', variants);
        return variants;
    }

    onChangeVariantGroupSubcategory(event, variantGroupItem) {
        if (variantGroupItem.variantIds) {
            const isSubcategoryChanged = variantGroupItem.variantIds.some(variantId => !event.variantIds.includes(variantId));
            if (isSubcategoryChanged) {
                variantGroupItem.variantIds = [];
            }
        }
    }

    onRemoveVariantGroupVariant(event, variantGroupItem) {
        console.log('event, variantGroupItem', event, variantGroupItem);
        const selectedVariantIndex = this.selectedVariantsByVariantGroup.indexOf(event.value.id);
        delete this.selectedVariantsByVariantGroup[selectedVariantIndex];
    }

    dropdawnOnAdd(event, groupName, keyName, value) {
        const {id} = event;
        this.state[groupName].forEach(item => {
            if (item.id === id) {
                item[keyName] = value;
            }
        });
    }

    getCurrentLocale() {
        return this.countries.find(c => c.language === this.selectedLanguage).locales[0];
    }

    dropdawnOnRemove(event, groupName, keyName) {
        const {id} = event.value;
        this.state[groupName].forEach(item => {
            if (item.id === id) {
                delete item[keyName];
            }
        });
    }

    openExportSettingsDialog() {
        this.exportSettingsModalRef = this.modalService.show(this.exportSettingsFormTemplate);
    }

    closeExportSettingsDialog() {
        this.exportSettingsModalRef.hide();
        this.selectedLocale = LOCALE_CONFIG.DEFAULT_LOCALE;
    }

    saveFile() {
        this.isExporting = true;
        this.configurationService.getExport(this.productsToExport, this.showPrice, this.selectedLocale).subscribe(response => {
            this.saveToFileSystem(response);
            this.exportSettingsModalRef.hide();
        });
    }

    private async saveToFileSystem(response) {
        const fileContent = JSON.stringify(response, null, 2);
        const blob = new Blob([fileContent], {type: 'text/plain'});
        saveAs(blob, 'configurator-' + moment().format('YYYYMMDD-HHmm') + '.json');
        this.isExporting = false;
        const successMessage = await this.translateService
            .get('webpanel.products.export.success_message')
            .toPromise();
        this.toasterService.pop('success', '', successMessage);
    }

    fileUpload({target}) {
        this.isUploading = true;
        const fileField = target;
        const file = fileField.files[0];
        const reader = new FileReader();
        reader.onload = () => {
            const result = JSON.parse(reader.result);
            this.configurationService.uploadImport(result).subscribe(async (response) => {
                this.isUploading = false;
                if (response.success) {
                    const successMessage = await this.translateService
                        .get('webpanel.products.import.success_message')
                        .toPromise();
                    this.toasterService.pop('success', '', successMessage);
                } else {
                    const errorMessage = await this.translateService
                        .get('webpanel.products.import.error_message')
                        .toPromise();
                    this.toasterService.pop('error', '', errorMessage);
                }
                fileField.value = '';
            });
        };
        reader.readAsText(file);
    }

    addNewItem(stateKey) {
        this.state[stateKey].push({
            edit: true,
        });
    }

    private createChangesObservation(settings) {
        this.changesCount = 0;
        this.haveChanges = false;
        const subject = this.datachanges.observe(settings);
        this.changesSubject = subject;
        subject.onChanges.subscribe((event) => {
            this.changesCount = event.subject.changesCount;
            this.haveChanges = this.changesCount > 0;
        });
        return subject.getProxy();
    }

    cancelChanges() {
        this.getConfigData();
    }

    deleteItems(groupName) {
        const idsForDelete = [];
        this.state[groupName] = this.state[groupName].filter((item, index) => {
            if (this.checkedItems[groupName].indexOf(index) === -1) {
                return true;
            } else {
                idsForDelete.push(item.id);
                return false;
            }
        });

        if (idsForDelete.length) {
            const deleteData = idsForDelete.join(',');
            this.configurationService.delete(this.complianceList[groupName], deleteData).subscribe(res => {
                // console.log(res);
            });

        }
        this.checkedItems[groupName] = [];
    }

    toggleItem({target}, itemGroup, itemIndex, itemId?: string) {
        if (target.checked) {
            this.checkedItems[itemGroup] = [...this.checkedItems[itemGroup] || [], itemIndex];
            if (itemGroup === 'products') {
                this.productsToExport.push(itemId);
            }
        } else {
            this.checkedItems[itemGroup].splice(
                this.checkedItems[itemGroup].indexOf(itemIndex),
                1
            );
            if (itemGroup === 'products') {
                this.productsToExport.splice(this.productsToExport.indexOf(itemId), 1);
            }
        }
    }

    editItem(item) {
        item.edit = true;
    }

    saveItem(itemName, item) {
        const locale = this.getCurrentLocale();
        if (item.edit && item.id) {
            this.updateItem(locale, itemName, item);
        }
        if (!item.id) {
            this.createItem(locale, itemName, item);
        }
        this.dataSaving = true;
        this.changesCount = 0;
        this.haveChanges = false;
        delete item.edit;
    }

    private createItem(locale: any, itemName: any, item: any) {
        this.configurationService.createItem(locale, itemName, item).subscribe(
            async (res) => {
                // TODO: try move it below, into complete()
                this.getConfigData();
                if (res.success) {
                    const successMessage = await this.translateService.get('webpanel.configuration.save.item.success_message').toPromise();
                    this.toasterService.pop('success', '', successMessage);
                }
            },
            async (error) => {
                this.getConfigData();
                const errorMessage = await this.translateService.get('webpanel.configuration.save.item.error_message').toPromise();
                this.toasterService.pop('error', errorMessage, error.message);
            },
            () => {
                // this.getConfigData();
            }
        );
    }

    private updateItem(locale: any, itemName: any, item: any) {
        this.configurationService.updateItem(locale, itemName, item).subscribe(
            async (res) => {
                this.getConfigData();
                if (res.success) {
                    const successMessage = await this.translateService
                        .get('webpanel.configuration.save.item.success_message')
                        .toPromise();
                    this.toasterService.pop('success', '', successMessage);
                }
            },
            async (error) => {
                this.getConfigData();
                const errorMessage = await this.translateService
                    .get('webpanel.configuration.save.item.error_message')
                    .toPromise();
                this.toasterService.pop('error', errorMessage, error.message);
            },
            () => {
                // this.getConfigData();
            }
        );
    }

    saveAll() {
        Object.keys(this.state)
            .forEach(stateSectionName => {
                if (this.state[stateSectionName] instanceof Array) {
                    this.state[stateSectionName]
                        .forEach(item => {
                            this.saveItem(this.complianceList[stateSectionName], item);
                        });
                }
            });
    }
}



// WEBPACK FOOTER //
// ./src/app/pages/configuration/configuration.component.ts